import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Plus, Trash2, Save, X, CalendarDays, MapPin } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, Spinner, Card, PrimaryButton, SecondaryButton, Input, Textarea, EmptyState } from "./_ui";

const EMPTY = { title: "", description: "", location: "", starts_at: "", image_url: "" };

export default function EvenementsPanel() {
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/events").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title || !form.location || !form.starts_at) return toast.error("Titre, lieu et date sont requis");
    setBusy(true);
    try {
      const payload = { ...form, starts_at: new Date(form.starts_at).toISOString() };
      await api.post("/admin/events", payload);
      toast.success("Événement créé");
      setAdding(false); setForm(EMPTY); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet événement ?")) return;
    await api.delete(`/admin/events/${id}`);
    toast.success("Supprimé"); await load();
  };

  if (!items) return (<><PageHeader title="Événements" /><Spinner /></>);

  return (
    <div data-testid="admin-events">
      <PageHeader
        title="Événements"
        subtitle="Manifestations, cérémonies et rendez-vous officiels du mouvement."
        action={
          !adding && (
            <PrimaryButton onClick={() => setAdding(true)} data-testid="event-create-btn">
              <Plus size={16} /> Nouvel événement
            </PrimaryButton>
          )
        }
      />

      {adding && (
        <Card className="p-5 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input placeholder="Titre" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title" />
            <Input placeholder="Lieu" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="event-location" />
            <Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="event-date" />
            <Input placeholder="URL image (facultatif)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
          </div>
          <Textarea placeholder="Description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-3" />
          <div className="flex gap-3 mt-4">
            <PrimaryButton onClick={save} disabled={busy} data-testid="event-save"><Save size={16} /> Enregistrer</PrimaryButton>
            <SecondaryButton onClick={() => { setAdding(false); setForm(EMPTY); }}><X size={16} /> Annuler</SecondaryButton>
          </div>
        </Card>
      )}

      {items.length === 0 ? (
        <EmptyState icon={CalendarDays} title="Aucun événement" hint="Programmez le premier événement A.F.B." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((e) => {
            const d = new Date(e.starts_at);
            return (
              <Card key={e.id} className="p-5 flex gap-4" data-testid={`event-${e.id}`}>
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#D4AF37]/30 to-[#0052CC]/20 border border-[#D4AF37]/40 grid place-items-center text-[#D4AF37] font-display text-2xl shrink-0">
                  {d.getDate()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display text-lg text-white truncate">{e.title}</h3>
                    <button onClick={() => remove(e.id)} className="text-slate-500 hover:text-[#CE1126] shrink-0" data-testid={`event-delete-${e.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="text-xs text-slate-400 mt-1 flex items-center gap-3">
                    <span>{d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
                    <span className="inline-flex items-center gap-1"><MapPin size={11} /> {e.location}</span>
                  </div>
                  {e.description && <p className="text-sm text-slate-300 mt-2 line-clamp-2">{e.description}</p>}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
